import { useRef, useState } from 'react';
import Papa from 'papaparse';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/Table';
import { importThemes, ThemeImportReport, ThemeImportRow } from '@/api/themesService';
import { toaster } from '@/components/ui/toaster';

interface ThemeImportModalProps {
  open: boolean;
  onClose: () => void;
  onImported: () => void;
}

const stripAccents = (value: string) =>
  value.normalize('NFD').replace(/[̀-ͯ]/g, '').trim().toLowerCase();

const pick = (row: Record<string, string>, matcher: (key: string) => boolean): string => {
  const key = Object.keys(row).find((k) => matcher(stripAccents(k)));
  return key ? (row[key] ?? '').trim() : '';
};

const mapRows = (parsed: Record<string, string>[]): ThemeImportRow[] =>
  parsed.map((row) => ({
    label: pick(row, (k) => k.includes('theme') || k.includes('libelle') || k === 'label' || k === 'nom'),
    identifier: pick(row, (k) => k.includes('identif')),
    description: pick(row, (k) => k.includes('description')),
    sports: pick(row, (k) => k.includes('sport'))
      .split('|')
      .map((s) => s.trim())
      .filter(Boolean),
  }));

const ThemeImportModal = ({ open, onClose, onImported }: ThemeImportModalProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState('');
  const [rows, setRows] = useState<ThemeImportRow[]>([]);
  const [report, setReport] = useState<ThemeImportReport | null>(null);
  const [busy, setBusy] = useState(false);

  const reset = () => {
    setFileName('');
    setRows([]);
    setReport(null);
    setBusy(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFile = (file: File) => {
    setReport(null);
    setFileName(file.name);
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: true,
      complete: async (result) => {
        const mapped = mapRows(result.data).filter((r) => r.label || r.sports.length > 0);
        setRows(mapped);
        if (mapped.length === 0) {
          toaster.create({ title: 'Aucune ligne exploitable dans le fichier', type: 'error' });
          return;
        }
        setBusy(true);
        try {
          setReport(await importThemes(mapped, true));
        } catch {
          toaster.create({ title: 'Analyse du fichier impossible', type: 'error' });
        } finally {
          setBusy(false);
        }
      },
      error: () => toaster.create({ title: 'Lecture du fichier impossible', type: 'error' }),
    });
  };

  const handleCommit = async () => {
    setBusy(true);
    try {
      const result = await importThemes(rows, false);
      toaster.create({
        title: `${result.summary.created} créé(s), ${result.summary.updated} mis à jour`,
        type: 'success',
      });
      onImported();
      handleClose();
    } catch {
      toaster.create({ title: 'Import impossible', type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      size="xl"
      title="Importer des thématiques (CSV)"
      footer={
        <>
          <Button variant="outline" onClick={handleClose}>
            Annuler
          </Button>
          <Button onClick={handleCommit} disabled={!report || report.summary.resolved === 0 || busy}>
            {busy ? 'Import…' : `Importer ${report?.summary.resolved ?? 0} thématique(s)`}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <div
          onClick={() => inputRef.current?.click()}
          className="flex cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border border-dashed border-gray-300 py-8 text-center text-sm text-gray-500 hover:border-brand-500 dark:border-gray-700"
        >
          <p className="font-medium text-gray-700 dark:text-gray-200">{fileName || 'Déposer le fichier .csv'}</p>
          <p>Colonnes attendues : thème, identifiant, description, sports (séparés par « | »)</p>
          <input
            ref={inputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
          />
        </div>

        {report && (
          <>
            <div className="flex flex-wrap gap-3 text-sm">
              <Badge color="green">{report.summary.resolved} valides</Badge>
              <Badge color="red">{report.summary.unresolved} en erreur</Badge>
              <Badge color="gray">{report.summary.total} lignes</Badge>
            </div>
            <div className="max-h-72 overflow-y-auto rounded-xl border border-gray-200 dark:border-gray-800">
              <Table>
                <THead>
                  <tr>
                    <TH>Statut</TH>
                    <TH>Libellé</TH>
                    <TH>Identifiant</TH>
                    <TH>Sports</TH>
                    <TH>Détail</TH>
                  </tr>
                </THead>
                <TBody>
                  {report.rows.map((r) => (
                    <TR key={r.index}>
                      <TD>
                        {r.status === 'ok' ? (
                          <Badge color={r.action === 'update' ? 'yellow' : 'green'}>
                            {r.action === 'update' ? 'màj' : 'créé'}
                          </Badge>
                        ) : (
                          <Badge color="red">erreur</Badge>
                        )}
                      </TD>
                      <TD>{r.label || '—'}</TD>
                      <TD>{r.identifier || '—'}</TD>
                      <TD>{r.sports.join(', ') || '—'}</TD>
                      <TD className="text-gray-500">{r.reason ?? '—'}</TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default ThemeImportModal;
